// BoxDialog.jsx – Fenster „Schachtel“ im Admin-Editor
// Erstellt das Druckbild des Schachteldeckels (aufgeklappt) für diesen Stammbaum:
// Titel (Name auf der Spielebox), Erweiterung ja/nein, Logo mit den Familienfarben,
// Anzahl Karten, Anzahl Familienstämme und Art.-Nr. Das Bild selbst baut utils/boxDesign.js.
// Download als SVG (Vektor, für die Druckerei am besten) oder als PNG (4000 px breit).
import React, { useEffect, useMemo, useState } from 'react';
import { buildBoxSvg, svgToPng, fileToDataUrl, BOX_W, BOX_H } from '../utils/boxDesign';
import { scWidth } from '../utils/smallCaps';
import fontUrl from '../fonts/Bandeleart-Regular.woff2';

// Heutiges Datum als Vorschlag für die Art.-Nr., z.B. 20261104-01
function defaultArtNr() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-01`;
}

// Textbreite in Kapitälchen mit der echten Schrift messen (für das Verkleinern langer Titel)
const ctx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
const measure = (text, size) => scWidth(ctx, text, size, 'Bandeleart');

function download(blob, name) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

// Props: defaultTitle, families (Farben der Familienstämme), cards (Anzahl Karten),
// defaultExtension (bestellt als Erweiterung → Häkchen gesetzt), onClose
export function BoxDialog({ defaultTitle = '', families = [], cards = 72, defaultExtension = false, onClose }) {
  const [title, setTitle] = useState(defaultTitle);
  const [extension, setExtension] = useState(defaultExtension);
  const [artNr, setArtNr] = useState(defaultArtNr);
  const [cardCount, setCardCount] = useState(cards);
  const [fontDataUrl, setFontDataUrl] = useState('');
  const [busy, setBusy] = useState(false);

  // Schrift einmal laden: als data:-Adresse (wird ins SVG eingebettet) und für das Messen
  useEffect(() => {
    let alive = true;
    Promise.all([
      fileToDataUrl(fontUrl),
      document.fonts.load('60px Bandeleart').catch(() => {}),
    ]).then(([url]) => { if (alive) setFontDataUrl(url); });
    return () => { alive = false; };
  }, []);

  const svg = useMemo(() => fontDataUrl ? buildBoxSvg({
    title: title.trim(), extension, families, cards: cardCount, artNr: artNr.trim(), fontDataUrl, measure,
  }) : '', [title, extension, families, cardCount, artNr, fontDataUrl]);

  const fileBase = 'Schachtel_' + (title.trim() || 'Stammbaum').replace(/[^\wäöüÄÖÜ-]+/g, '_') + (extension ? '_Erweiterung' : '');

  const downloadSvg = () => download(new Blob([svg], { type: 'image/svg+xml' }), fileBase + '.svg');
  const downloadPng = async () => {
    setBusy(true);
    try { download(await svgToPng(svg, 4000), fileBase + '.png'); }
    finally { setBusy(false); }
  };

  const label = { display: 'block', fontSize: 13, color: '#555', marginBottom: 4 };
  const input = { width: '100%', padding: '7px 9px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: '22px 22px 18px', width: 'min(900px, 100%)',
        maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Schachteldeckel</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={label}>Titel auf der Schachtel</label>
            <input style={input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Familie Muster" />
          </div>
          <div>
            <label style={label}>Art.-Nr.</label>
            <input style={input} value={artNr} onChange={e => setArtNr(e.target.value)} />
          </div>
          <div>
            <label style={label}>Anzahl Karten</label>
            <input style={input} type="number" min={2} step={2} value={cardCount}
              onChange={e => setCardCount(Math.max(0, parseInt(e.target.value, 10) || 0))} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginTop: 20 }}>
            <input type="checkbox" checked={extension} onChange={e => setExtension(e.target.checked)} />
            Erweiterung
          </label>
        </div>

        <div style={{ fontSize: 12, color: '#777', marginBottom: 10 }}>
          {families.length} {families.length === 1 ? 'Familienstamm' : 'Familienstämme'} – das Logo zeigt
          {' '}{Math.min(6, Math.max(2, families.length))} Karten in den Familienfarben.
        </div>

        <div style={{ background: '#f3f3f3', borderRadius: 8, padding: 8, marginBottom: 14 }}>
          {svg
            ? <img alt="Vorschau Schachtel" src={'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)}
                style={{ width: '100%', aspectRatio: `${BOX_W} / ${BOX_H}`, display: 'block' }} />
            : <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Schrift wird geladen…</div>}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={downloadSvg} disabled={!svg}>↓ SVG</button>
          <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={downloadPng} disabled={!svg || busy}>
            {busy ? 'Wird erstellt…' : '↓ PNG (4000 px)'}
          </button>
        </div>
      </div>
    </div>
  );
}
